/** Display-formatting helpers used across the whole SPA.
 *
 * Dates/times render in TWO dimensions that must NOT follow the browser:
 *   - **language** — the locale the user picked in the UI (`i18n.language`),
 *     not `navigator.language`. A French UI on an en-US laptop must still
 *     show `13/07/2026 19:06`, never `7/13/2026, 7:06 PM`.
 *   - **time zone** — the active organisation's IANA tz (stored UTC →
 *     rendered in the org tz), set via `setDisplayTimeZone()` from a
 *     top-level effect when `/auth/me` loads. Undefined → the browser tz.
 *
 * So every absolute date/time goes through `formatDate` / `formatDateTime`
 * / `formatTime` here — never a bare `new Date(x).toLocaleString()`, which
 * silently uses the browser locale + browser tz.
 */
import i18n from "@/lib/i18n";

/* ---------------- active locale + org time zone ---------------- */

let _displayTimeZone: string | undefined;

/** Set the IANA tz absolute dates render in (the active org's `timezone`).
 * Call from a top-level effect on `/auth/me` load. `""`/`null` → browser tz;
 * an explicit `"UTC"` renders in UTC (the org's configured value wins). */
export function setDisplayTimeZone(tz: string | null | undefined): void {
  _displayTimeZone = tz && tz.trim() ? tz.trim() : undefined;
}

/** The tz absolute dates currently render in (undefined = browser tz). */
export function getDisplayTimeZone(): string | undefined {
  return _displayTimeZone;
}

function activeLocale(): string {
  return i18n.language || "fr";
}

/** Locale-aware currency (default EUR). Renders in the picked UI language, so
 * the symbol position + separators follow the locale (en → "€25.29",
 * fr/de → "25,29 €"). Returns "—" for null/NaN. */
export function formatCurrency(
  value: number | null | undefined,
  currency = "EUR",
): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  return new Intl.NumberFormat(activeLocale(), {
    style: "currency",
    currency,
  }).format(value);
}

/** Caller `opts` fully REPLACE the defaults (matching how the original
 * `toLocaleString(locale, opts)` calls behaved — a `{ month: "long" }`
 * label must not inherit a stray numeric day). The org tz is always added. */
function zonedOpts(
  defaults: Intl.DateTimeFormatOptions,
  opts?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatOptions {
  const chosen = opts ?? defaults;
  return _displayTimeZone
    ? { timeZone: _displayTimeZone, ...chosen }
    : { ...chosen };
}

type DateInput = string | number | Date | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value == null || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/* ---------------- duration (no date / tz) ---------------- */

/** "14m 32s" / "1h 04m 12s" / "—" for null. */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds == null || seconds < 0) return "—";
  const s = Math.floor(seconds % 60);
  const m = Math.floor(seconds / 60) % 60;
  const h = Math.floor(seconds / 3600);
  if (h > 0) return `${h}h ${pad(m)}m ${pad(s)}s`;
  if (m > 0) return `${m}m ${pad(s)}s`;
  return `${s}s`;
}

function pad(n: number): string {
  return n.toString().padStart(2, "0");
}

/* ---------------- absolute dates (locale + org tz) ---------------- */

/** Localised date, org tz. Default numeric (e.g. `13/07/2026`). Pass
 * `opts` to override (e.g. `{ weekday: "long", month: "long" }`). */
export function formatDate(
  value: DateInput,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleDateString(
    activeLocale(),
    zonedOpts({ year: "numeric", month: "2-digit", day: "2-digit" }, opts),
  );
}

/** Localised date + time (24-h where the locale dictates), org tz.
 * e.g. `13/07/2026 19:06`. */
export function formatDateTime(
  value: DateInput,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleString(
    activeLocale(),
    zonedOpts(
      {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      },
      opts,
    ),
  );
}

/** Localised time only, org tz. e.g. `19:06`. */
export function formatTime(
  value: DateInput,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(value);
  if (!d) return "—";
  return d.toLocaleTimeString(
    activeLocale(),
    zonedOpts({ hour: "2-digit", minute: "2-digit" }, opts),
  );
}

/** Calendar day-key `YYYY-MM-DD` of an instant, in the display (org) tz — so
 * a calendar buckets an event on the org's wall-clock day, not the browser's
 * (a 01:00 Paris session viewed from a US browser is org-tz "the 21st", not
 * browser-tz "the 20th"). Locale-independent (formatToParts, not en-CA). No
 * org tz set → the browser's local day. */
export function formatDayKey(value: DateInput): string {
  const d = toDate(value);
  if (!d) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    ...(_displayTimeZone ? { timeZone: _displayTimeZone } : {}),
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/* ---------------- relative ("il y a 5 min" / "5m ago") ---------------- */

/** Coarse localised relative delta via `Intl.RelativeTimeFormat`. Falls
 * back to the absolute localised date/time for anything over ~7 days. */
export function formatRelative(value: DateInput): string {
  const d = toDate(value);
  if (!d) return "—";
  const diffSec = Math.round((d.getTime() - Date.now()) / 1000); // <0 = past
  const abs = Math.abs(diffSec);
  const rtf = new Intl.RelativeTimeFormat(activeLocale(), { numeric: "auto" });
  if (abs < 60) return rtf.format(diffSec, "second");
  const min = Math.round(diffSec / 60);
  if (Math.abs(min) < 60) return rtf.format(min, "minute");
  const hr = Math.round(min / 60);
  if (Math.abs(hr) < 24) return rtf.format(hr, "hour");
  const day = Math.round(hr / 24);
  if (Math.abs(day) < 7) return rtf.format(day, "day");
  return formatDateTime(d);
}
